/**
 * Pre-upload QA wire schemas. The QA engine (backend/src/qa/engine.ts) is
 * pure — it consumes a StoredReport + photo manifests + the case and
 * emits a QaReport. The admin UI renders the report as rule rows with
 * pass/fail/warn chips; approve-for-upload requires `overall !== 'fail'`.
 *
 * Rule ids are STABLE — the admin UI and any future analytics key on them:
 *   coversheet-complete, consent-captured, hardware-photos, checklist-photos,
 *   missing-clause-photos, photo-timestamps, nonconformance-assessments,
 *   assessment-alignment, combo-duplicate-noncompliance
 *
 * `missing-clause-photos` warns (it does not fail) for an answered item with
 * no photo, except Compliant, Not Sighted, Not Applicable, N/V and Minor.
 * Approval can proceed only after an explicit acknowledgement and a reason.
 *
 * `combo-duplicate-noncompliance` warns (never fails) when a PVD/BTY combo
 * partner report shares the same non-compliance wording (or clause id).
 */
import { z } from 'zod';
export const QaStatusSchema = z.enum(['pass', 'fail', 'warn']);
export const QaRuleResultSchema = z.object({
    /** Stable rule id (see module docblock). */
    ruleId: z.string().min(1),
    /** Human label for the rule row. */
    label: z.string().min(1),
    status: QaStatusSchema,
    /** What failed/warned, specific enough to act on. Omitted on clean passes. */
    detail: z.string().optional(),
});
export const QaReportSchema = z.object({
    jobId: z.string(),
    /** Which submission revision was assessed. */
    submittedAt: z.string(),
    /** When the engine ran. */
    generatedAt: z.string(),
    /** fail if any rule failed; else warn if any warned; else pass. */
    overall: QaStatusSchema,
    results: z.array(QaRuleResultSchema),
});
/** Fold rule results into the overall verdict. */
export function overallQaStatus(results) {
    if (results.some((r) => r.status === 'fail'))
        return 'fail';
    if (results.some((r) => r.status === 'warn'))
        return 'warn';
    return 'pass';
}
//# sourceMappingURL=qa.js.map